import { alpha, type Theme } from '@mui/material';
import type { TTaskServiceKey, TTaskSlaPulseLevel, TTaskUrgencyKey } from './types.ts';

type TTasksTableCellSizeKey =
  'reference' | 'subject' | 'task' | 'deadline' | 'sla' | 'status' | 'executor' | 'action';

type TTaskUrgencyColors = {
  accent: string;
  background: string;
  text: string;
};

const slaPulseStyleByLevel: Record<
  TTaskSlaPulseLevel,
  { duration: string; opacity: number; radius: number; size: number }
> = {
  calm: { duration: '2.4s', opacity: 0.18, radius: 4, size: 6 },
  notice: { duration: '2s', opacity: 0.26, radius: 6, size: 7 },
  alert: { duration: '1.55s', opacity: 0.34, radius: 8, size: 8 },
  urgent: { duration: '1.15s', opacity: 0.42, radius: 10, size: 9 },
  critical: { duration: '0.85s', opacity: 0.52, radius: 12, size: 10 },
  overdue: { duration: '0.7s', opacity: 0.6, radius: 14, size: 10 },
};

export const tasksPageStyles = {
  tokens: {
    panelHeaderBackground: '#f5f9fc',
    hoverBackground: '#f8fafc',
    selectedBackground: '#eef6ff',
    selectedHoverBackground: '#e4f1ff',
    sectionTitleColor: '#334155',
    mutedIconColor: '#6b7280',
    activeIconColor: '#0f4c81',
  },
  panelFrame: (theme: Theme) => ({
    border: `1px solid ${theme.palette.border.subtle}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
  }),
  urgencyColors: (theme: Theme, urgency: TTaskUrgencyKey): TTaskUrgencyColors => {
    const errorColor = theme.palette.covenantStatus.error ?? theme.palette.error.main;
    const warningColor = theme.palette.covenantStatus.warning ?? theme.palette.warning.main;
    const mutedTextColor = theme.palette.text.muted ?? theme.palette.text.secondary;
    const colors: Record<TTaskUrgencyKey, TTaskUrgencyColors> = {
      now: {
        accent: errorColor,
        background: alpha(errorColor, 0.08),
        text: errorColor,
      },
      soon: {
        accent: warningColor,
        background: alpha(warningColor, 0.1),
        text: warningColor,
      },
      later: {
        accent: mutedTextColor,
        background: theme.palette.background.default,
        text: mutedTextColor,
      },
    };

    return colors[urgency];
  },
  pulseDot: (theme: Theme, keyframesName: string, radius = 6) => ({
    [`@keyframes ${keyframesName}`]: {
      '0%': {
        boxShadow: `0 0 0 0 ${alpha(theme.palette.covenantStatus.error, 0.38)}`,
      },
      '70%': {
        boxShadow: `0 0 0 ${radius}px ${alpha(theme.palette.covenantStatus.error, 0)}`,
      },
      '100%': {
        boxShadow: `0 0 0 0 ${alpha(theme.palette.covenantStatus.error, 0)}`,
      },
    },
    animation: `${keyframesName} 1.8s ease-out infinite`,
  }),
  pulseBorder: (theme: Theme, keyframesName: string) => ({
    [`@keyframes ${keyframesName}`]: {
      '0%': {
        borderColor: alpha(theme.palette.covenantStatus.error, 0.45),
        boxShadow: `0 0 0 0 ${alpha(theme.palette.covenantStatus.error, 0.18)}`,
      },
      '70%': {
        borderColor: alpha(theme.palette.covenantStatus.error, 0.85),
        boxShadow: `0 0 0 4px ${alpha(theme.palette.covenantStatus.error, 0)}`,
      },
      '100%': {
        borderColor: alpha(theme.palette.covenantStatus.error, 0.45),
        boxShadow: `0 0 0 0 ${alpha(theme.palette.covenantStatus.error, 0)}`,
      },
    },
    animation: `${keyframesName} 1.8s ease-out infinite`,
  }),
  urgencyRowPulse: (theme: Theme, urgency: TTaskUrgencyKey, keyframesName: string) => {
    const accent = tasksPageStyles.urgencyColors(theme, urgency).accent;

    return {
      [`@keyframes ${keyframesName}`]: {
        '0%': {
          boxShadow: `inset 3px 0 0 ${accent}`,
        },
        '50%': {
          boxShadow: `inset 5px 0 0 ${accent}`,
        },
        '100%': {
          boxShadow: `inset 3px 0 0 ${accent}`,
        },
      },
      animation: `${keyframesName} 1.8s ease-in-out infinite`,
    };
  },
  pageWrap: (theme: Theme) => ({
    minHeight: '100%',
    backgroundColor: theme.palette.common.white,
    py: { xs: theme.spacing(2), sm: theme.spacing(3) },
  }),
  pageContainer: (theme: Theme) => ({
    maxWidth: 'min(1520px, 100%)',
    px: { xs: theme.spacing(2), sm: theme.spacing(3), lg: theme.spacing(4) },
  }),
  header: (theme: Theme) => ({
    mb: { xs: theme.spacing(2.5), sm: theme.spacing(3) },
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  }),
  title: {
    fontWeight: 700,
    color: 'text.primary',
    fontSize: { xs: '1.75rem', sm: '2rem' },
    lineHeight: 1.15,
  },
  subtitle: {
    color: 'text.secondary',
    fontSize: '0.9375rem',
    lineHeight: '22px',
  },
  contentGrid: (theme: Theme) => ({
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '248px minmax(0, 1fr)' },
    alignItems: 'start',
    gap: { xs: theme.spacing(2), md: theme.spacing(3) },
  }),
  roleColumn: (theme: Theme) => ({
    minWidth: 0,
    position: { xs: 'static', md: 'sticky' },
    top: { md: theme.spacing(14) },
  }),
  content: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: theme.spacing(2), sm: theme.spacing(2.5) },
    minWidth: 0,
  }),
  emptyState: (theme: Theme) => ({
    ...tasksPageStyles.panelFrame(theme),
    px: { xs: theme.spacing(2), sm: theme.spacing(3) },
    py: { xs: theme.spacing(3), sm: theme.spacing(4) },
  }),
  emptyTitle: {
    color: 'text.primary',
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: 1.3,
  },
  emptyText: (theme: Theme) => ({
    mt: theme.spacing(0.75),
    color: 'text.secondary',
    fontSize: '0.9375rem',
    lineHeight: '22px',
  }),
  rolePanel: (theme: Theme) => ({
    ...tasksPageStyles.panelFrame(theme),
    overflow: 'hidden',
  }),
  rolePanelHeader: (theme: Theme) => ({
    px: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
    py: { xs: theme.spacing(1.25), sm: theme.spacing(1.5) },
    borderBottom: `1px solid ${theme.palette.border.subtle}`,
    backgroundColor: tasksPageStyles.tokens.panelHeaderBackground,
  }),
  rolePanelTitle: {
    color: 'text.primary',
    fontSize: '0.9375rem',
    fontWeight: 800,
    lineHeight: 1.25,
  },
  roleList: (theme: Theme) => ({
    display: 'flex',
    flexDirection: { xs: 'row', md: 'column' },
    flexWrap: { xs: 'wrap', md: 'nowrap' },
    gap: { xs: theme.spacing(1), md: 0 },
    p: { xs: theme.spacing(1.25), md: 0 },
    overflowX: { xs: 'auto', md: 'visible' },
  }),
  roleItem: (isSelected: boolean) => (theme: Theme) => ({
    width: { xs: 'auto', md: '100%' },
    minWidth: { xs: 192, md: 0 },
    minHeight: 52,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(0.5),
    px: { xs: theme.spacing(1), md: theme.spacing(1.25) },
    py: theme.spacing(1),
    textAlign: 'left',
    borderRadius: { xs: theme.spacing(0.75), md: 0 },
    borderBottom: { xs: 'none', md: `1px solid ${alpha(theme.palette.border.subtle, 0.6)}` },
    backgroundColor: isSelected
      ? tasksPageStyles.tokens.selectedBackground
      : theme.palette.common.white,
    color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
    transition: 'background-color 0.2s ease, color 0.2s ease',
    '&:hover': {
      backgroundColor: isSelected
        ? tasksPageStyles.tokens.selectedHoverBackground
        : tasksPageStyles.tokens.hoverBackground,
    },
    '&.Mui-disabled': {
      opacity: 1,
      color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
    },
    '&:last-of-type': {
      borderBottom: 'none',
    },
  }),
  roleRadio: {
    flexShrink: 0,
    p: 0.5,
  },
  roleTextWrap: (theme: Theme) => ({
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
  }),
  roleLabel: {
    color: 'inherit',
    fontSize: '0.9375rem',
    fontWeight: 700,
    lineHeight: 1.25,
    overflowWrap: 'anywhere',
  },
  roleMeta: {
    color: 'text.secondary',
    fontSize: '0.8125rem',
    lineHeight: 1.25,
  },
  sectionPaper: (theme: Theme) => ({
    ...tasksPageStyles.panelFrame(theme),
    overflow: 'hidden',
  }),
  sectionToggle: (theme: Theme) => ({
    width: '100%',
    display: 'flex',
    alignItems: { xs: 'flex-start', sm: 'center' },
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    px: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
    py: { xs: theme.spacing(1.25), sm: theme.spacing(1.5) },
    textAlign: 'left',
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: tasksPageStyles.tokens.hoverBackground,
    },
  }),
  sectionToggleContent: (theme: Theme) => ({
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    alignItems: 'flex-start',
  }),
  sectionTitle: () => ({
    fontWeight: 700,
    color: tasksPageStyles.tokens.sectionTitleColor,
    fontSize: { xs: '1rem', sm: '1.125rem' },
    lineHeight: 1.3,
  }),
  sectionMetaWrap: (theme: Theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0,
  }),
  sectionMeta: {
    color: 'text.secondary',
    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
    lineHeight: 1.4,
  },
  sectionToggleIcon: (isExpanded: boolean) => ({
    color: isExpanded
      ? tasksPageStyles.tokens.activeIconColor
      : tasksPageStyles.tokens.mutedIconColor,
    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.2s ease',
    alignSelf: { xs: 'center', sm: 'auto' },
  }),
  sectionContent: (theme: Theme) => ({
    borderTop: `1px solid ${theme.palette.border.subtle}`,
  }),
  tableContainer: {
    backgroundColor: '#ffffff',
    overflowX: 'auto',
  },
  table: (serviceKey: TTaskServiceKey) => ({
    width: '100%',
    minWidth:
      serviceKey === 'guarantees' ? { xs: 920, sm: 980, md: 1060 } : { xs: 720, sm: 900, md: 900 },
    borderCollapse: 'separate',
    borderSpacing: 0,
    tableLayout: 'fixed',
    backgroundColor: '#ffffff',
  }),
  tableHead: (theme: Theme) => ({
    backgroundColor: alpha(theme.palette.primary['200'] ?? theme.palette.primary.light, 0.24),
  }),
  tableHeadCell: (theme: Theme, columnKey: string) => ({
    px: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
    py: { xs: theme.spacing(1.125), sm: theme.spacing(1.375) },
    borderBottom: `1px solid ${alpha(theme.palette.border.subtle, 0.9)}`,
    backgroundColor: alpha(theme.palette.primary['200'] ?? theme.palette.primary.light, 0.24),
    color: theme.palette.text.secondary,
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '0.8125rem',
    lineHeight: 1.25,
    fontWeight: 800,
    letterSpacing: 0,
    whiteSpace: 'nowrap',
    ...(columnKey === 'actions' || columnKey === 'action'
      ? {
          width: '14%',
        }
      : {}),
  }),
  tableHeaderFilter: (theme: Theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.25),
  }),
  tableHeaderIconButton: (isActive: boolean) => (theme: Theme) => ({
    p: theme.spacing(0.25),
    color: isActive
      ? tasksPageStyles.tokens.activeIconColor
      : tasksPageStyles.tokens.mutedIconColor,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  }),
  tableBodyCell: (theme: Theme, columnKey: string) => ({
    px: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
    py: { xs: theme.spacing(1.25), sm: theme.spacing(1.5) },
    borderBottom: `1px solid ${alpha(theme.palette.border.subtle, 0.55)}`,
    verticalAlign: 'middle',
    color: theme.palette.text.primary,
    fontSize: '0.9375rem',
    lineHeight: '22px',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    ...(columnKey === 'actions' || columnKey === 'action'
      ? {
          whiteSpace: 'nowrap',
        }
      : {}),
  }),
  tableRow: (theme: Theme) => ({
    backgroundColor: theme.palette.common.white,
    transition: 'background-color 0.16s ease',
    '&:hover': {
      backgroundColor: tasksPageStyles.tokens.hoverBackground,
    },
    '&.MuiTableRow-hover:hover': {
      backgroundColor: tasksPageStyles.tokens.hoverBackground,
    },
    '&:last-of-type td': {
      borderBottom: 0,
    },
  }),
  emptyTableCell: (theme: Theme) => ({
    py: theme.spacing(4),
    px: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: '0.9375rem',
    lineHeight: '22px',
    borderBottom: 0,
  }),
  guaranteeCounterpartyMeta: (theme: Theme) => ({
    color: theme.palette.text.secondary,
    display: 'block',
    mt: theme.spacing(0.5),
    fontSize: '0.8125rem',
    lineHeight: 1.3,
  }),
  taskActionsMenuPaper: {
    width: 'auto',
    minWidth: 'max-content',
  },
  taskActionsMenuItem: (theme: Theme) => ({
    minHeight: 36,
    px: theme.spacing(1.5),
    py: theme.spacing(0.75),
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  }),
  assigneeDialog: (theme: Theme) => ({
    '& .MuiDialog-paper': {
      borderRadius: theme.spacing(1),
    },
  }),
  assigneeDialogTitle: {
    color: 'text.primary',
    fontSize: '1.125rem',
    fontWeight: 800,
    lineHeight: 1.3,
  },
  assigneeDialogContent: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    pt: `${theme.spacing(1)} !important`,
  }),
  assigneeDialogLoading: (theme: Theme) => ({
    minHeight: 84,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
  }),
  assigneeDialogText: {
    color: 'text.secondary',
    fontSize: '0.9375rem',
    lineHeight: '22px',
  },
  assigneeDialogError: (theme: Theme) => ({
    color: theme.palette.error.main,
    fontSize: '0.875rem',
    lineHeight: '20px',
  }),
  assigneeDialogActions: (theme: Theme) => ({
    px: theme.spacing(3),
    pb: theme.spacing(2.5),
    gap: theme.spacing(1),
  }),
  tablePagination: (theme: Theme) => ({
    borderTop: `1px solid ${theme.palette.border.subtle}`,
    backgroundColor: theme.palette.common.white,
  }),
  tableCellSize: {
    reference: { width: '16%' },
    subject: { width: '26%' },
    task: { width: '28%' },
    deadline: { width: '14%' },
    sla: { width: '14%' },
    status: { width: '16%' },
    executor: { width: '16%' },
    action: { width: '12%' },
  } as Record<TTasksTableCellSizeKey, { width: string }>,
  guaranteeSlaRow: (isOverdue: boolean) => (theme: Theme) =>
    isOverdue
      ? {
          boxShadow: `inset 4px 0 0 ${theme.palette.covenantStatus.error}`,
          backgroundColor: alpha(theme.palette.covenantStatus.error, 0.035),
          '&:hover': {
            backgroundColor: alpha(theme.palette.covenantStatus.error, 0.07),
          },
          '&.MuiTableRow-hover:hover': {
            backgroundColor: alpha(theme.palette.covenantStatus.error, 0.07),
          },
        }
      : {},
  taskRow: (urgency: TTaskUrgencyKey) => (theme: Theme) => {
    if (urgency === 'later') {
      return { boxShadow: 'none' };
    }

    if (urgency === 'now') {
      return tasksPageStyles.urgencyRowPulse(theme, urgency, 'tasksRowUrgencyPulse');
    }

    return {
      boxShadow: `inset 3px 0 0 ${tasksPageStyles.urgencyColors(theme, urgency).accent}`,
    };
  },
  deadlineWrap: (theme: Theme) => ({
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 112,
  }),
  slaCellPlaceholder: {
    color: 'text.disabled',
    fontSize: '0.875rem',
    lineHeight: 1.3,
  },
  slaWrap: (isOverdue: boolean) => (theme: Theme) => ({
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 118,
    px: theme.spacing(1),
    py: theme.spacing(0.75),
    borderRadius: theme.spacing(0.75),
    border: `1px solid ${
      isOverdue
        ? alpha(theme.palette.covenantStatus.error, 0.6)
        : alpha(theme.palette.warning.main, 0.34)
    }`,
    backgroundColor: isOverdue
      ? alpha(theme.palette.covenantStatus.error, 0.08)
      : alpha(theme.palette.warning.main, 0.06),
  }),
  slaHeader: (theme: Theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0,
  }),
  slaPulseDot: (level: TTaskSlaPulseLevel) => (theme: Theme) => {
    const pulse = slaPulseStyleByLevel[level];
    const color =
      level === 'calm' || level === 'notice'
        ? theme.palette.warning.main
        : theme.palette.covenantStatus.error;

    return {
      [`@keyframes tasksSlaPulse-${level}`]: {
        '0%': {
          boxShadow: `0 0 0 0 ${alpha(color, pulse.opacity)}`,
          transform: 'scale(1)',
        },
        '70%': {
          boxShadow: `0 0 0 ${pulse.radius}px ${alpha(color, 0)}`,
          transform: level === 'calm' ? 'scale(1.05)' : 'scale(1.18)',
        },
        '100%': {
          boxShadow: `0 0 0 0 ${alpha(color, 0)}`,
          transform: 'scale(1)',
        },
      },
      width: pulse.size,
      height: pulse.size,
      borderRadius: '50%',
      backgroundColor: color,
      flexShrink: 0,
      animation: `tasksSlaPulse-${level} ${pulse.duration} ease-out infinite`,
      transformOrigin: 'center',
    };
  },
  slaTextWrap: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  slaDate: {
    color: 'text.primary',
    fontSize: '0.8125rem',
    fontWeight: 700,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
  },
  slaCountdown: (isOverdue: boolean) => (theme: Theme) => ({
    color: isOverdue ? theme.palette.covenantStatus.error : theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: isOverdue ? 800 : 700,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
  }),
  slaOverdueText: (theme: Theme) => ({
    color: theme.palette.covenantStatus.error,
    fontSize: '0.75rem',
    fontWeight: 800,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
  }),
  guaranteeStatusChip: (theme: Theme) => ({
    maxWidth: 136,
    height: 'auto',
    minHeight: 27,
    '& .MuiChip-label': {
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
      textAlign: 'center',
      lineHeight: '16px',
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
    },
  }),
  urgentTaskBadge: (theme: Theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    width: 'fit-content',
    minHeight: 22,
    mt: theme.spacing(0.75),
    px: theme.spacing(1),
    py: theme.spacing(0.25),
    borderRadius: theme.spacing(0.75),
    border: `1px solid ${alpha(theme.palette.covenantStatus.error, 0.42)}`,
    color: theme.palette.covenantStatus.error,
    backgroundColor: alpha(theme.palette.covenantStatus.error, 0.08),
    fontSize: '0.75rem',
    fontWeight: 800,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
  }),
  urgencyIndicator: (urgency: TTaskUrgencyKey) => (theme: Theme) => {
    const colors = tasksPageStyles.urgencyColors(theme, urgency);

    return {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(0.75),
      minHeight: 24,
      px: theme.spacing(1),
      py: theme.spacing(0.25),
      borderRadius: 999,
      border: `1px solid ${alpha(colors.accent, urgency === 'later' ? 0.28 : 0.5)}`,
      color: colors.text,
      backgroundColor: colors.background,
      whiteSpace: 'nowrap',
      ...(urgency === 'now' ? tasksPageStyles.pulseBorder(theme, 'tasksUrgencyBorderPulse') : {}),
    };
  },
  urgencyDot: (urgency: TTaskUrgencyKey) => (theme: Theme) => ({
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: tasksPageStyles.urgencyColors(theme, urgency).accent,
    flexShrink: 0,
    ...(urgency === 'now' ? tasksPageStyles.pulseDot(theme, 'tasksUrgencyPulse') : {}),
  }),
  sectionUrgencyDot: (theme: Theme) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.covenantStatus.error,
    flexShrink: 0,
    ...tasksPageStyles.pulseDot(theme, 'tasksSectionUrgencyPulse'),
  }),
  urgencyLabel: {
    color: 'inherit',
    fontSize: '0.75rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
} as const;
